import { useMemo } from "react"
import { Check, PlusCircle } from "lucide-react"
import { Badge } from "@components/ui/badge"
import { Button } from "@components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover"
import { Separator } from "@components/ui/separator"
import { cn } from "@components/ui/lib/utils"
import type { SelectFilterProps } from "./types"
import { strictFilter as defaultStrictFilter, sortOptions } from "./utils"

export function SelectFilter(props: SelectFilterProps) {
  const {
    value,
    onChange,
    options,
    placeholder,
    className,
    sortSelectedFirst = true,
    customFilter,
    facets,
  } = props

  // Determine filter function to use
  const filterFn = customFilter || defaultStrictFilter

  // Get sorted options
  const sortedOptions = useMemo(() => {
    if (!sortSelectedFirst) return options
    return sortOptions(options, value)
  }, [options, sortSelectedFirst, value])

  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "tw:flex tw:w-full tw:justify-start tw:gap-2 tw:border-border",
            className,
          )}
        >
          <PlusCircle />
          {placeholder || "Select..."}
          {value && (
            <>
              <Separator orientation="vertical" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal"
              >
                {selectedOption?.label || value}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw:w-fit tw:p-0" align="start">
        <Command filter={filterFn}>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {value && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange(undefined)}
                    className="tw:cursor-pointer tw:justify-center tw:text-center"
                  >
                    Clear filter
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {sortedOptions.map((option) => {
                const isSelected = value === option.value
                return (
                  <CommandItem
                    key={String(option.value)}
                    value={option.label}
                    onSelect={() => {
                      onChange(isSelected ? undefined : option.value)
                    }}
                    className="tw:cursor-pointer"
                  >
                    <div
                      className={cn(
                        "tw:mr-2 tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-sm tw:border tw:border-primary",
                        isSelected
                          ? "tw:bg-primary"
                          : "tw:opacity-50 tw:[&_svg]:invisible",
                      )}
                    >
                      <Check className="tw:size-4 tw:text-primary-foreground" />
                    </div>
                    {option.icon && (
                      <option.icon className="tw:mr-2 tw:size-4 tw:text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                    {facets?.get(option.value) && (
                      <span className="tw:ml-auto tw:flex tw:size-4 tw:items-center tw:justify-center tw:text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
